import React, { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import videojs from "video.js";

const resolveSourceType = (url) => {
  const clean = (url || "").split("?")[0].toLowerCase();
  if (clean.endsWith(".m3u8")) return "application/x-mpegURL";
  if (clean.endsWith(".mpd")) return "application/dash+xml";
  if (clean.endsWith(".webm")) return "video/webm";
  if (clean.endsWith(".ogg") || clean.endsWith(".ogv")) return "video/ogg";
  return "video/mp4";
};

export const ShortsVideoPlayer = forwardRef(function ShortsVideoPlayer(
  { src, poster, muted = true, volume = 80, onPlay, onError, onTime, onPlayingChange, onMutedChange },
  ref
) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const appliedSrcRef = useRef(src);
  const callbacks = useRef({});
  callbacks.current = { onPlay, onError, onTime, onPlayingChange, onMutedChange };

  useEffect(() => {
    const videoEl = document.createElement("video");
    videoEl.classList.add("video-js", "vjs-fill");
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("controlsList", "nodownload noplaybackrate");
    videoEl.setAttribute("disablePictureInPicture", "");
    videoEl.disablePictureInPicture = true;
    videoEl.oncontextmenu = (e) => e.preventDefault();
    containerRef.current.appendChild(videoEl);

    const player = videojs(videoEl, {
      autoplay: "any",
      muted: muted,
      loop: true,
      controls: false,
      bigPlayButton: false,
      playsinline: true,
      preload: "auto",
      fluid: false,
      sources: src ? [{ src, type: resolveSourceType(src) }] : [],
      poster: poster || "",
    });
    playerRef.current = player;
    player.volume((volume || 0) / 100);

    const report = () =>
      callbacks.current.onTime && callbacks.current.onTime(player.currentTime() || 0, player.duration() || 0);

    player.on("play", () => {
      callbacks.current.onPlayingChange && callbacks.current.onPlayingChange(true);
      callbacks.current.onPlay && callbacks.current.onPlay();
    });
    player.on("pause", () => callbacks.current.onPlayingChange && callbacks.current.onPlayingChange(false));
    player.on("volumechange", () => callbacks.current.onMutedChange && callbacks.current.onMutedChange(player.muted()));
    player.on("timeupdate", report);
    player.on("loadedmetadata", report);
    player.on("error", () => callbacks.current.onError && callbacks.current.onError());

    return () => {
      if (player && !player.isDisposed()) player.dispose();
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !src) return;
    player.poster(poster || "");
    if (appliedSrcRef.current === src) return;
    appliedSrcRef.current = src;
    if (player.error()) player.error(null);
    player.src([{ src, type: resolveSourceType(src) }]);
    const playback = player.play();
    if (playback && playback.catch) playback.catch(() => {});
  }, [src, poster]);

  useEffect(() => {
    const player = playerRef.current;
    if (player) player.muted(muted);
  }, [muted]);

  useEffect(() => {
    const player = playerRef.current;
    if (player) player.volume((volume || 0) / 100);
  }, [volume]);

  useImperativeHandle(
    ref,
    () => ({
      toggle() {
        const p = playerRef.current;
        if (!p) return;
        if (p.paused()) p.play();
        else p.pause();
      },
      seekFrac(f) {
        const p = playerRef.current;
        if (!p) return;
        const d = p.duration() || 0;
        if (d) p.currentTime(Math.max(0, Math.min(1, f)) * d);
      },
      seekBy(delta) {
        const p = playerRef.current;
        if (!p) return;
        const d = p.duration() || 0;
        const t = (p.currentTime() || 0) + delta;
        p.currentTime(d ? Math.max(0, Math.min(d, t)) : Math.max(0, t));
      },
    }),
    []
  );

  return (
    <div data-vjs-player className="dk-vjs" onContextMenu={(e) => e.preventDefault()}>
      <div ref={containerRef} className="dk-vjs-mount" />
    </div>
  );
});

export default ShortsVideoPlayer;
